<template>
  <TModalCritical
    v-model:visible="visible"
    :title="currentTitle"
    size="lg"
    enable-scroll
    :label-tertiary="!isFirstStep ? t('actions.back') : undefined"
    @action:tertiary="handleHelpMeSearchBack"
  >
    <div class="space-y-6">
      <TProgress
        :value="currentProgress"
        size="sm"
      />

      <div
        class="grid gap-4"
        :class="{
          'grid-cols-2': !currentStepContent?.spanColumnsOptions,
          'grid-cols-3': currentStepContent?.spanColumnsOptions,
        }"
      >
        <button
          v-for="option in currentStepContent?.options"
          :key="option.label"
          class="p-4 w-full border border-gray-200 text-primary-950 rounded-lg has-checked:border-primary-800 hover:bg-gray-50 has-focus:border-primary-800 cursor-pointer transition-colors font-semibold"
          :class="{
            'bg-primary-50 hover:bg-primary-100 outline outline-secondary border-secondary font-semibold': selectedOptions[currentStep] === option.value,
          }"
          @click="setOptionForCurrentStep(currentStep, option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <template v-if="currentStepContent?.name === 'how'">
        <TTipbox
          v-if="selectedOptions.how === 'towable'"
          variant="highlight"
        >
          {{ t('helpMeSearch.how.options.towable.tip') }}
        </TTipbox>

        <TCard v-if="selectedOptions.how === 'drivable'">
          <TToggle
            v-model="unlimitedMileage"
            :label="t('helpMeSearch.how.options.drivable.toggleLabel')"
            :description="t('helpMeSearch.how.options.drivable.toggleDescription')"
            size="sm"
          />
        </TCard>

        <TTipbox
          v-if="selectedOptions.how === 'delivery'"
          variant="highlight"
        >
          {{ t('helpMeSearch.how.options.delivery.tip') }}
        </TTipbox>
      </template>
    </div>

    <template #button-primary="{ dismiss }">
      <TButton
       
        :disabled="!canProceed"
        @click="handleHelpMeSearchApply(dismiss)"
      >
        {{ primaryLabel }}
      </TButton>
    </template>
  </TModalCritical>
</template>

<script setup lang="ts">
import { COOKIES } from '#shared/constants/cookies'

const visible = defineModel<boolean>('visible', { default: false })

const { t } = useI18n()
const { navigateToRvSearch } = useSearch()
const { pageSource } = usePageSource()

type Step = | 'festival' | 'guests' | 'how' | 'pets'
type SelectedOptions = {
  guests?: number
  pets?: boolean
  how?: string
  festival?: boolean
}

type HelpWizardCookie = {
  step: Step
  options: SelectedOptions
  unlimitedMileage?: boolean
}

const cookie = useCookie<Nullable<HelpWizardCookie>>(COOKIES.searchHelpWizard)

const steps = ref<Step[]>(['guests', 'pets', 'festival', 'how'])

function isValidStep(step: string | undefined): step is Step {
  return step !== undefined && steps.value.includes(step as Step)
}

function getInitialSelectedOptions(): SelectedOptions {
  return {
    guests: undefined,
    pets: undefined,
    how: undefined,
    festival: undefined,
  }
}

const savedCookie = cookie.value
const currentStep = ref<Step>(isValidStep(savedCookie?.step) ? savedCookie.step : steps.value[0]!)
const isFirstStep = computed(() => currentStep.value === steps.value[0])

const hasStartedWizard = ref(false)
const shouldPersistWizard = ref(true)
const unlimitedMileage = ref(savedCookie?.unlimitedMileage ?? false)

const currentTitle = computed(() => t(`helpMeSearch.${currentStep.value}.title`))

const primaryLabel = computed(() => {
  const nextStep = steps.value[steps.value.indexOf(currentStep.value) + 1]

  if (!nextStep) {
    return t('actions.showResults')
  }

  return t('actions.next')
})

const stepsContent = computed(() => ([
  {
    name: 'how',
    spanColumnsOptions: true,
    options: [
      {
        label: t('helpMeSearch.how.options.towable.label'),
        value: 'towable',
      }, {
        label: t('helpMeSearch.how.options.drivable.label'),
        value: 'drivable',
      }, {
        label: t('helpMeSearch.how.options.delivery.label'),
        value: 'delivery',
      },
    ],
  },
  {
    name: 'guests',
    options: [{
      label: '1-2',
      value: 2,
    }, {
      label: '3-4',
      value: 4,
    }, {
      label: '5-6',
      value: 6,
    }, {
      label: '7+',
      value: 7,
    }],
  },
  {
    name: 'pets',
    options: [{
      label: t('common.yes'),
      value: true,
    }, {
      label: t('common.no'),
      value: false,
    }],
  },
  {
    name: 'festival',
    options: [{
      label: t('common.yes'),
      value: true,
    }, {
      label: t('common.no'),
      value: false,
    }],
  },
]))

const currentStepContent = computed(() => stepsContent.value.find((step) => step.name === currentStep.value))

const currentProgress = computed(() => {
  return (steps.value.indexOf(currentStep.value) / steps.value.length) * 100
})

const selectedOptions = ref<SelectedOptions>(isValidStep(savedCookie?.step) && savedCookie?.options
  ? savedCookie.options
  : getInitialSelectedOptions())

const isLastStep = computed(() => !steps.value[steps.value.indexOf(currentStep.value) + 1])
const canProceed = computed(() => selectedOptions.value[currentStep.value] !== undefined)

watch([currentStep, selectedOptions, unlimitedMileage], () => {
  if (!shouldPersistWizard.value) {
    return
  }

  cookie.value = {
    step: currentStep.value,
    options: selectedOptions.value,
    unlimitedMileage: unlimitedMileage.value,
  }
}, { deep: true })

function setOptionForCurrentStep<K extends keyof SelectedOptions>(
  key: K,
  option: SelectedOptions[K],
) {
  if (!hasStartedWizard.value) {
    trackWizardStarted()
  }

  selectedOptions.value[key] = option

  if (!isLastStep.value) {
    goToNextStep()
  }
}

function goToNextStep() {
  const nextStep = steps.value[steps.value.indexOf(currentStep.value) + 1]

  if (!nextStep) {
    return
  }

  currentStep.value = nextStep
}

function goToPreviousStep() {
  const previousStep = steps.value[steps.value.indexOf(currentStep.value) - 1]

  if (!previousStep) {
    return
  }

  currentStep.value = previousStep
}

async function setSearchParameters() {
  const how = selectedOptions.value.how

  await navigateToRvSearch({
    Adults: selectedOptions.value.guests ? Number(selectedOptions.value.guests) : undefined,
    Children: 0,
    Delivery: how === 'delivery',
    Drivable: how === 'drivable',
    FestivalFriendly: selectedOptions.value.festival,
    PetFriendly: selectedOptions.value.pets,
    Towable: how === 'towable',
    UnlimitedMileage: how === 'drivable' ? unlimitedMileage.value : false,
  })

  trackWizardFinished()
  resetWizard()
}

function trackWizardStarted() {
  hasStartedWizard.value = true

  trackSearchWizardStarted({
    pageSource: pageSource.value,
  })
}

function trackWizardFinished() {
  const { guests, pets, how } = selectedOptions.value

  if (guests === undefined || pets === undefined || how === undefined) {
    return
  }

  trackSearchWizardCompleted({
    pageSource: pageSource.value,
    responses: {
      guests: guests,
      allowPet: pets,
      destination: '',
      rvSelection: how,
    },
  })
}

function resetWizard() {
  shouldPersistWizard.value = false
  currentStep.value = steps.value[0]!
  unlimitedMileage.value = false
  selectedOptions.value = getInitialSelectedOptions()
  hasStartedWizard.value = false
  cookie.value = null

  nextTick(() => {
    shouldPersistWizard.value = true
  })
}

function applySearchAndClose(dismiss?: () => void) {
  setSearchParameters()

  if (dismiss) {
    dismiss()
  }
  else {
    visible.value = false
  }
}

function handleHelpMeSearchApply(dismiss: () => void) {
  if (isLastStep.value) {
    applySearchAndClose(dismiss)
    return
  }

  goToNextStep()
}

function handleHelpMeSearchBack() {
  goToPreviousStep()
}
</script>

<i18n src="~~/i18n/locales/common/search/help-widget.json" lang="json" />
